import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface FilterOptions {
  tickers: string[];
  channels: string[];
}

export function useFilterOptions() {
  return useQuery({
    queryKey: ['filter-options'],
    queryFn: async (): Promise<FilterOptions> => {
      const [tickersResult, channelsResult] = await Promise.all([
        supabase.from('distinct_tickers').select('ticker').limit(1000),
        supabase.from('distinct_channels').select('channel_name').limit(500),
      ]);

      if (tickersResult.error) {
        console.error('Supabase error (tickers):', tickersResult.error);
        throw new Error('数据加载失败');
      }
      if (channelsResult.error) {
        console.error('Supabase error (channels):', channelsResult.error);
        throw new Error('数据加载失败');
      }

      return {
        tickers: (tickersResult.data || []).map((r: { ticker: string }) => r.ticker).filter(Boolean),
        channels: (channelsResult.data || []).map((r: { channel_name: string }) => r.channel_name).filter(Boolean),
      };
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

// Find the most recent date that has viewpoints for a given ticker
// Returns date in YYYYMMDD format
export async function findLatestDateForTicker(ticker: string): Promise<string | null> {
  // Sanitize: only allow alphanumeric, dash, dot, caret (for ^VIX etc)
  const sanitized = ticker.toUpperCase().replace(/[^A-Z0-9.\-^]/g, '');
  if (!sanitized) return null;

  const { data, error } = await supabase
    .from('ticker_viewpoints')
    .select('date')
    .eq('ticker', sanitized)
    .order('date', { ascending: false })
    .limit(1);

  if (error) {
    console.error('Supabase error:', error);
    return null;
  }
  if (!data || data.length === 0) {
    return null;
  }

  // Convert from YYYY-MM-DD to YYYYMMDD format
  const dbDate = (data[0] as { date: string }).date;
  return dbDate.replace(/-/g, '');
}
